import { ArtworkImage } from './ArtworkImage';
import { useGateBackPath } from './GateArtwork';
import React, { useId, useState } from 'react';
import { ClueResult, GateResult, Monster, MonsterMovement, MythosBreakdown as MythosBreakdownType } from '../api';
import { GateMarker } from './GateMarker';
import { InvestigatorMarker } from './InvestigatorMarker';
import { Monsters } from './Monsters';

import './MythosBreakdown.css';
import { SimpleMarkdown } from './SimpleMarkdown';

type MythosBreakdownProps = {
    mythosDetails: MythosBreakdownType;
    mythosBreakdown?: string[];
}

// "Open", "Surge", "Blocked", "Burst Open", "DoomTokenIncrease"
type GateProps = {
    gateResult: GateResult;
}
const Gate: React.FC<GateProps> = ({ gateResult }) => {
  const gateBackPath = useGateBackPath();
    if (!gateResult.result && !gateResult.location?.name) {
        return null;
    }

    const isSurge = gateResult.result === 'Surge';
    const isDoomTokenIncrease = gateResult.result === 'DoomTokenIncrease';
    const isBlocked = gateResult.result === 'Blocked';
    const doomTokensAdded = Math.max(1, gateResult.doomTokensAdded ?? 1);

    return (
        <div className="mythos-gate">
            {gateResult.isBurst && <div className='gate-burst'>BURST</div>}
            {!gateResult.isBurst && isSurge && <div className='gate-surge'>Surge</div>}
            {!gateResult.isBurst && isBlocked && <div className='gate-blocked'>Blocked</div>}
            {gateResult.result === 'Open' && <div>Open</div>}
            {isDoomTokenIncrease ? (
                <div className='mythos-doom-track-eyes'>
                    {Array.from({ length: doomTokensAdded }, (_, idx) => (
                        <div key={`gate-doom-track-eye-${idx}`} className='mythos-doom-track-eye'>
                            <div className='doom-track-eye' />
                        </div>
                    ))}
                </div>
            ) : gateResult.gate ? <GateMarker gate={gateResult.gate} focusable /> : <img loading="lazy" alt="Gates deck" className="deck-image gate-back-artwork" src={gateBackPath} style={{
                width: '60px',
                height: '60px'
            }} />}
            {gateResult.location && <div className={gateResult.isBurst ? 'gate-burst' : 'mythos-location'}>{gateResult.location.name}</div>}
        </div>
    );
}

// "Location", "Blocked" or "Investigator"
type ClueProps = {
    clueResult: ClueResult;
}
const Clue: React.FC<ClueProps> = ({ clueResult }) => {
    if (clueResult.blocked) {
        return null;
    }

    if (clueResult.investigator) {
        return (
            <div className='mythos-clue-wrapper'>
                <ArtworkImage loading="lazy" className='mythos-clue' src="/Clue.jpg" alt="Clue" style={{borderRadius:'50%'}} />
                <div className='mythos-clue-investigator'>
                    <InvestigatorMarker investigator={clueResult.investigator} />
                </div>
            </div>
        )
    }

    if (clueResult.location?.name) {
        return (
            <div className='mythos-clue-wrapper'>
                <ArtworkImage loading="lazy" className='mythos-clue' src="/Clue.jpg" alt="Clue" style={{borderRadius:'50%'}} />
                <div className='mythos-location'>{clueResult.location.name}</div>
            </div>
        );
    }

    return null;
}

type MonstersAppearedProps = {
    monsters: Monster[];
}
const MonstersAppeared: React.FC<MonstersAppearedProps> = ({ monsters }) => {
    return (
        <div className='mythos-monsters-wrapper'>
            <Monsters wrapperClass="mythos-monsters" monsters={monsters} />
            <div>New Monsters</div>
        </div>
    );
}

type MonstersMovedProps = {
    monsters: Monster[];
}
const MonstersMoved: React.FC<MonstersMovedProps> = ({ monsters }) => {
    return (
        <div className='mythos-monsters-wrapper'>
            <Monsters wrapperClass="mythos-monsters" monsters={monsters} />
            <div>Monsters moved</div>
        </div>
    );
}

type DoomTrackIncreaseProps = {
    amount: number;
}
const DoomTrackIncrease: React.FC<DoomTrackIncreaseProps> = ({ amount }) => {
    return (
        <div className='mythos-doom-track-eyes'>
            {Array.from(Array(amount).keys()).map((_, idx) => (
                <div key={`doom-track-eye-${idx}`} className='mythos-doom-track-eye'>
                    <div className='doom-track-eye' />
                </div>
            ))}
        </div>
    )
}

type TerrorLevelIncreaseProps = {
    amount: number;
}
export const TerrorLevelIncrease: React.FC<TerrorLevelIncreaseProps> = ({ amount }) => {
    return (
        <div className='mythos-terror-level-eyes' role="img" aria-label={`Terror level ${amount}`}>
            {Array.from(Array(amount).keys()).map((_, idx) => (
                <div key={`terror-level-eye-${idx}`} className='mythos-terror-level-eye'>
                    <div className='terror mythos-terror' />
                </div>
            ))}
        </div>
    )
}

type MythosMonsterMovementProps = {
    movement: MonsterMovement[];
}
const MythosMonsterMovement: React.FC<MythosMonsterMovementProps> = ({ movement }) => {
    if (movement.length === 0) {
        return null;
    }

    return (
        <div className='mythos-monster-movement-column'>
            <div className='mythos-monster-movement-row white'>
                {movement.filter(m => m.color === 'White').map((m) => (
                    <img loading="lazy" key={`white-dimension-sign-${m.dimensionSign}`} alt={m.dimensionSign} src={`/signs/${m.dimensionSign}.svg`} />
                ))}
            </div>
            <div className='mythos-monster-movement-row black'>
                {movement.filter(m => m.color === 'Black').map((m) => (
                    <img loading="lazy" key={`black-dimension-sign-${m.dimensionSign}`} alt={m.dimensionSign} src={`/signs/${m.dimensionSign}.svg`} />
                ))}
            </div>
        </div>
    );
}

const normalizeGateResults = (gateValue: unknown): GateResult[] => {
    if (Array.isArray(gateValue)) {
        return gateValue as GateResult[];
    }

    if (gateValue && typeof gateValue === 'object') {
        return [gateValue as GateResult];
    }

    return [];
};

export const MythosBreakdown: React.FC<MythosBreakdownProps> = ({ mythosDetails, mythosBreakdown }) => {
    const breakdownId = useId();
    // Polling replaces arrays, so identify the resolution by content rather than reference.
    const breakdownKey = JSON.stringify([mythosDetails.name, mythosBreakdown ?? []]);
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const [previousBreakdownKey, setPreviousBreakdownKey] = useState(breakdownKey);
    if (previousBreakdownKey !== breakdownKey) {
        setPreviousBreakdownKey(breakdownKey);
        setExpandedKey(null);
    }
    const isExpanded = expandedKey === breakdownKey;
    const visibleBreakdown = isExpanded ? mythosBreakdown : mythosBreakdown?.slice(0, 3);
    const gateResults = normalizeGateResults(mythosDetails.gate);
    const clues = mythosDetails.clues ?? [];
    const appearedMonsters = mythosDetails.appearedMonsters ?? [];
    const movedMonsters = mythosDetails.movedMonsters ?? [];
    const doomTrackIncrease = mythosDetails.doomTrackIncrease ?? 0;
    const terrorLevelIncrease = mythosDetails.terrorLevelIncrease ?? 0;
    const doomTokensShownInGateResults = gateResults.reduce((sum, gateResult) => {
        if (gateResult.result !== 'DoomTokenIncrease') {
            return sum;
        }

        return sum + Math.max(0, gateResult.doomTokensAdded ?? 0);
    }, 0);
    const remainingDoomTrackIncrease = Math.max(0, doomTrackIncrease - doomTokensShownInGateResults);
    const rumorSections = [
        ['Pass', mythosDetails.rumorPassCondition],
        ['Reward', mythosDetails.rumorPassReward],
        ['Fail', mythosDetails.rumorFailCondition],
        ['Penalty', mythosDetails.rumorFailPenalty],
    ] as const;
    const effectText = [
        mythosDetails.effect,
        ...rumorSections
            .filter(([, value]) => value?.trim())
            .map(([label, value]) => `**${label}**: ${value}`),
    ].filter((text) => text?.trim()).join('\n');

    return (
        <div className="mythos-card">
            {!!mythosDetails.name && (
                <div className='mythos-name'>{mythosDetails.name}</div>
            )}
            <div className='mythos-row'>
                {mythosDetails.name !== 'The Story Continues...' && gateResults.map((gateResult, idx) => (
                    <Gate
                        key={`gate-${idx}-${gateResult.location?.name ?? gateResult.result ?? 'unknown'}`}
                        gateResult={gateResult}
                    />
                ))}
                {appearedMonsters.length > 0 && <MonstersAppeared monsters={appearedMonsters} />}

                {clues.map((clue, idx) => (
                    <Clue key={`clue-${idx}`} clueResult={clue} />
                ))}
                
                {(mythosDetails.monsterMovement || []).length > 0 && <MythosMonsterMovement movement={mythosDetails.monsterMovement || []} />}
                {movedMonsters.length > 0 && <MonstersMoved monsters={movedMonsters} />}

                {remainingDoomTrackIncrease > 0 && <DoomTrackIncrease amount={remainingDoomTrackIncrease} />}

                {terrorLevelIncrease > 0 && <TerrorLevelIncrease amount={terrorLevelIncrease} />}
            </div>

            {!!effectText && (
                <div className='mythos-text'>
                    <SimpleMarkdown text={effectText} keyPrefix="mythos" />
                </div>
            )}

            {mythosBreakdown && (
                <div className='mythos-row'>
                    {mythosBreakdown.length > 0 && (
                        <div className="mythos-breakdown-banner">
                            <ul id={breakdownId} className="mythos-breakdown-list">
                            {visibleBreakdown?.map((entry, idx) => (
                                <li key={`${entry}-${idx}`} className="mythos-breakdown-item">
                                    {entry}
                                </li>
                            ))}
                            </ul>
                            {mythosBreakdown.length > 3 && (
                                <button type="button" className="mythos-breakdown-toggle"
                                    aria-expanded={isExpanded} aria-controls={breakdownId}
                                    onClick={() => setExpandedKey(isExpanded ? null : breakdownKey)}>
                                    {isExpanded ? 'Show less' : 'Show all'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
